import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { UtilsService } from './shared/utils.service';
import {environment} from "../environments/environment";
import { AuthService } from "./shared/auth.service";
import { AppService } from "./app.service";
import { MatDialog } from "@angular/material/dialog";
import { catchError, tap } from "rxjs/operators";
import { SURFACE_PERMISSION_ERRORS } from "./shared/http-context-tokens";

@Injectable({
  providedIn: 'root'
})
export class ErrorInterceptorService implements HttpInterceptor {

  constructor(
    private router: Router,
    private utils: UtilsService,
    private app: AppService,
    private auth: AuthService,
    private dialog: MatDialog
  ) { }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {

    // Add the platform to the request headers
    if (!request.headers.has('Platform')) {
      request = request.clone({
        headers: request.headers.set('Platform', 'Web')
      });
    }

    return next.handle(request).pipe(
      tap(() => { }),
      catchError((response: HttpErrorResponse) => {
        // Skip error handling for external (non-SiteConnect API) requests.
        // These are handled by the calling service (e.g., GeoLocationService fallback logic).
        const isExternalRequest = !request.url.startsWith(environment.API_URL)
          && !request.url.startsWith(environment.SERVICE_URL);
        if (isExternalRequest) {
          return throwError(() => response);
        }

        // Not found API error.
        if (response.status === 404 && !this.router.url.match(/sign-in/)) {
          this.utils.showToast(response?.error?.message || 'The requested resource was not found.');
          return throwError(() => response);
        }

        // User level errors.
        else if (
          response.status >= 400 &&
          response.status !== 401 &&
          !this.router.url.match(/sign-in/) &&
          response.status < 500
        ) {
          // Custom error response (no Laravel `errors` object). This is the shape returned by both
          // RBACPermissions::unauthorizedResponse() and PlanFeatures::upgradeRequiredResponse(),
          // which carry only a `message` (plus `upgrade_required` for the plan gate).
          if (!response?.error?.errors) {
            const customMessage = response?.error?.message || 'An error occurred.';

            // The request body was rejected as too large, so it never reached a controller.
            //
            // Handled before the chart and permission branches because this status is always worth
            // showing and its body is never worth showing: Laravel's ValidatePostSize middleware
            // answers with the developer facing "The POST data is too large." before routing, and an
            // nginx enforced limit answers with an HTML body carrying no message at all.
            //
            // Without this branch a 413 fell through to the rethrow at the end of this block with
            // nothing displayed. That made every oversized upload in the portal fail in silence,
            // because almost none of the ~25 makeUploadRequest() call sites attach a catch().
            // Keeping it here fixes all of them at once rather than one component at a time.
            if (response.status === 413) {
              this.utils.showModal('Upload Too Large', this.utils.getUploadTooLargeMessage());
              return throwError(() => response);
            }

            // Standalone chart pages show a modal for any error, including permission denials,
            // because the chart cannot render an inline message on these pages.
            if ( this.router.url.indexOf('/charts/') == 0 ) {
              this.utils.showModal('Error / Notice', customMessage);
              return throwError(() => response);
            }

            if (response.status === 403) {
              // An email verification block is not a permission denial, and it has its own UI: the sign
              // in screen opens a dialog offering to resend the verification link. Staying silent here
              // avoids a duplicate toast on top of that dialog.
              //
              // This branch is reached on the sign in screen despite the /sign-in/ guard above, because
              // the root route ('') renders the sign in page too and router.url is then just '/'.
              //
              // Nothing is lost for any other caller: this interceptor rethrows, so their own catch()
              // still reports the message through handleAPIErrors().
              if (response?.error?.verification_required) {
                return throwError(() => response);
              }

              // A blob response (downloads/exports) carries its error body as a Blob rather than
              // parsed JSON, so no message is available synchronously. Fall back to the standard
              // denial wording instead of a generic error.
              const deniedMessage = response?.error?.message
                || 'You do not have permission to perform this action.';

              // The plan gate flags its denial with `upgrade_required`, so always surface the
              // upgrade prompt. This covers cases the client-side PlanGuard cannot catch, such as a
              // subscription downgrade taking effect mid-session.
              if (response?.error?.upgrade_required) {
                this.utils.showToast(customMessage);
              }
              // RBAC denials are surfaced for state-changing requests (always user initiated) and
              // for GETs the caller explicitly marked as user initiated via SURFACE_PERMISSION_ERRORS
              // (exports, downloads). Background GET loads stay silent because their components
              // render an inline "no permission" state instead (e.g. dashboard chart widgets set
              // hasPermissionToView = false on a 403), and toasting each one would be noisy.
              else if (
                request.method !== 'GET'
                || request.context.get(SURFACE_PERMISSION_ERRORS)
              ) {
                this.utils.showToast(deniedMessage);
              }
            }

            return throwError(() => response);
          }

          // Laravel generated error response.
          let errorMessage = '';
          let errors = response.error.errors;
          const keys = Object.keys(errors);
          keys.forEach((errorGroup) => {
            for (let i = 0; i < errors[errorGroup].length; i++) {
              errorMessage += errors[errorGroup][i] + '<br>';
            }
          });
          this.utils.showModal('Error / Notice', errorMessage);
          return throwError(() => response);
        }

        // Redirect the user if an authenticated route is matched and the routes are not listed below.
        else if (response.status === 401 && (!this.router.url.match(/sign-in/) && !this.router.url.match(/login-credentials/) && !this.router.url.match(/handle-qr-code/))) {
          this.router.navigate([
            'sign-in',
            {
              redirect: this.router.url
            }
          ]);
          return throwError(() => response);
        }

        // Server errors.
        else if (response.status >= 500) {
          if (response.status == 503 && this.app.isAuthenticated) {
            // Server is in Maintenance Mode
            if (this.dialog) {
              this.dialog.closeAll(); // Close Dialogs
            }
            this.auth.signOut(this.utils.getServerErrorMessage());
          } else {
            this.utils.showModal(
              'Error / Notice',
              this.utils.getServerErrorMessage()
            );
          }
          return throwError(() => response);
        }

        // Failure to reach the server. The request never got an HTTP response, so the only thing the
        // browser hands us is its own TypeError ("Load failed" in Safari, "Failed to fetch" in Chrome).
        // The wording lives in UtilsService because handleAPIErrors() surfaces the same case for
        // callers whose catch() runs after this interceptor rethrows.
        else if (response.status === 0) {
          this.utils.showModal(
            'Cannot Reach our Server',
            this.utils.getConnectivityErrorMessage()
          );
          return throwError(() => response);
        }

        // Rethrow so callers see the error
        return throwError(() => response);
      })
    );
  }
}
